/**
 * A figure, and the row of figures a page opens with.
 *
 * The label sits *above* the value, small and tracked, and the value is large.
 * That order is the point: somebody scanning a dashboard reads the numbers
 * first and only looks for the label of the one that surprised them. Putting
 * the label first at body size makes every tile cost a full read.
 *
 * `tone` exists so one figure on a screen can be the one that matters. If two
 * are toned, neither is.
 */

import type { ReactNode } from "react";

import "./data.css";

export type StatTone = "neutral" | "accent" | "success" | "warning" | "danger";

export interface StatProps {
  label: string;
  value: ReactNode;
  /** A unit, a delta, or the denominator of a ratio. */
  hint?: string;
  tone?: StatTone;
}

export function Stat({ label, value, hint, tone = "neutral" }: StatProps) {
  return (
    <div className="hardauto-stat" data-tone={tone}>
      <span className="hardauto-eyebrow">{label}</span>
      <span className="hardauto-stat__value">{value}</span>
      {hint ? <span className="hardauto-stat__hint">{hint}</span> : null}
    </div>
  );
}

export interface StatGridProps {
  children: ReactNode;
  /** Announced to a screen reader as the group's name. */
  label?: string;
}

/** A row of figures that wraps, rather than a fixed number of columns. */
export function StatGrid({ children, label }: StatGridProps) {
  return (
    <div className="hardauto-stat-grid" role="group" aria-label={label}>
      {children}
    </div>
  );
}
